import { useEffect } from 'react'
import Lottie from 'lottie-react'
import { useTranslation } from 'react-i18next'
import submitCompleteAnimation from '@/assets/lottie/subjective-submit-complete.json'
import submitCompleteSound from '@/assets/sounds/subjective-submit-complete.mp3'

interface Props {
  isOpen: boolean
  onClose: () => void
}

export function PauseSubjectiveResultPopup({ isOpen, onClose }: Props) {
  const { t } = useTranslation('learner')

  useEffect(() => {
    if (!isOpen) return
    const timer = window.setTimeout(onClose, 3000)
    return () => window.clearTimeout(timer)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return

    let audio: HTMLAudioElement | null = null
    try {
      audio = new Audio(submitCompleteSound)
      audio.volume = 0.5
      audio.play().catch(() => {
        // 브라우저 자동 재생 정책에 막혀도 제출 완료 흐름은 유지한다.
      })
    } catch {
      // Audio를 지원하지 않는 환경에서도 팝업은 정상 노출한다.
    }

    return () => {
      if (!audio) return
      audio.pause()
      audio.currentTime = 0
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      role="status"
      aria-live="assertive"
      aria-label={t('pause.subjectiveSubmitted') as string}
      onClick={onClose}
      className="fixed inset-0 z-50 flex cursor-pointer items-center justify-center"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <Lottie
        data-testid="pause-subjective-result-lottie"
        animationData={submitCompleteAnimation}
        autoplay
        loop={false}
        aria-hidden="true"
        className="relative h-[360px] w-[360px] max-w-full"
      />
    </div>
  )
}
